import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  TAX_WITHHOLDING_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/taxWithholdingColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';
import { buildTaxWithholdingFilterGroups } from '../constants/taxWithholdingFiltersConfig';

const axios = require('axios').default;

const TAX_WITHHOLDING_CONFIG = {
  pageTitle: 'Tax Withholding',
  pageSubtitle: 'Tax withholding amounts across your events.',
  sidebarKey: 'finances_tax_withholding',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: TAX_WITHHOLDING_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesTaxWithholdingColumns_${teamId}`,
  firstColumnId: 'event_name',
  defaultSortRules: [{ field: 'event_date', direction: 'desc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/tax_withholding/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/tax_withholding/data.json`,
  detailResponseKey: 'rows',
  detailErrorMessage: 'Failed to load tax withholding data.',
  detailTableTitle: 'Tax Withholding',
  emptyMessage: 'No tax withholding settlements found in this range.',
  searchPlaceholder: 'Search tax withholding...',
  paginationLabel: 'settlements',
  itemLabel: (count) => `settlement${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    {
      label: 'Total Withheld',
      value: compactCurrency(current.total_withheld || 0),
      current: current.total_withheld || 0,
      prior: prior.total_withheld || 0,
    },
    {
      label: 'Settlements',
      value: (current.settlement_count || 0).toLocaleString(),
      current: current.settlement_count || 0,
      prior: prior.settlement_count || 0,
    },
    {
      label: 'Paid',
      value: compactCurrency(current.total_paid || 0),
      current: current.total_paid || 0,
      prior: prior.total_paid || 0,
    },
    {
      label: 'Unpaid',
      value: compactCurrency(current.total_unpaid || 0),
      current: current.total_unpaid || 0,
      prior: prior.total_unpaid || 0,
    },
  ],
  kpiGridColumns: 4,

  chartColor: '#1565c0',
  salesCardIsCurrency: true,
  salesCardTitle: 'Tax Withheld',
  buildMetricValue: () => '',
  buildMetricLabel: () => '',
  overTimeKey: null,
  countLabel: 'settlements',

  topCharts: [],

  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'event_date':
        return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'event_name':
        return row.event_name || '\u2014';
      case 'artist_name':
        return row.artist_name || '\u2014';
      case 'venue_name':
        return row.venue_name || '\u2014';
      case 'amount':
        return cf.format(row.amount || 0);
      case 'withholding_percentage':
        return row.withholding_percentage != null ? `${Number(row.withholding_percentage).toFixed(2)}%` : '\u2014';
      case 'due_at':
        return <span className="text-nowrap">{row.due_at ? formatEventDate(row.due_at) : '\u2014'}</span>;
      case 'paid_at':
        return <span className="text-nowrap">{row.paid_at ? formatEventDate(row.paid_at) : '\u2014'}</span>;
      case 'payment_method':
        return row.payment_method || '\u2014';
      case 'payment_status':
        return row.payment_status === 'Paid'
          ? <span className="badge" style={{ background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', fontSize: 12 }}>Paid</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)', fontSize: 12 }}>Unpaid</span>;
      default:
        return '\u2014';
    }
  },

  filterTitle: 'Filter Tax Withholding',
  filterSubtitle: 'Apply filters to narrow tax withholding data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.event_name && activeFilters.event_name.length > 0) {
    params.event_name = activeFilters.event_name;
  }
  if (activeFilters.payment_status && activeFilters.payment_status.length > 0) {
    params.payment_status = activeFilters.payment_status;
  }
  if (activeFilters.payment_method && activeFilters.payment_method.length > 0) {
    params.payment_method = activeFilters.payment_method;
  }
  return params;
};

function ReportingFinancesTaxWithholdingPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return;
    axios.get(`/teams/${team.id}/reporting/finances/tax_withholding/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => { console.error('Failed to load tax withholding filter options', err); });
  }, [team?.id]);

  const configWithFilters = {
    ...TAX_WITHHOLDING_CONFIG,
    buildFilterGroups: () => buildTaxWithholdingFilterGroups(filterOptions),
    buildDetailApiFilters,
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesTaxWithholdingPage;
